import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { logger, redactParams } from './common/utils/logger';
import Page1089 from './1089/Page1089';
import Page1089Success from './1089/Page1089Success';
import Page1089Failed from './1089/Page1089Failed';
import Page3346 from './3346/Page3346';
import Page3346Success from './3346/Page3346Success';
import Page3346Failed from './3346/Page3346Failed';
import Beeline1 from './beeline1/Beeline1';
import Beeline1Success from './beeline1/Beeline1Success';
import Beeline1Failed from './beeline1/Beeline1Failed';
import Beeline2 from './beeline2/Beeline2';
import Beeline2Success from './beeline2/Beeline2Success';
import Beeline2Failed from './beeline2/Beeline2Failed';
import Beeline3 from './beeline3/Beeline3';
import Beeline3Success from './beeline3/Beeline3Success';
import Beeline3Failed from './beeline3/Beeline3Failed';

/**
 * Human-readable page name per route — e.g. "LP 1089 — Landing" — so log
 * lines read like a product person's description of the page, not a raw
 * path.
 */
function resolvePageName(pathname) {
  const carrierMatch = pathname.match(/^\/(\d+|beeline\d+)\/(megacom|beeline|nur)\/(success|failed)$/);
  if (carrierMatch) {
    const [, lp, carrier, outcome] = carrierMatch;
    const carrierName = { megacom: 'MegaCom', beeline: 'Beeline', nur: 'Nur' }[carrier];
    return `LP ${lp} — ${carrierName} ${outcome === 'success' ? 'Success' : 'Failed'}`;
  }

  const successMatch = pathname.match(/^\/(\d+|beeline\d+)\/success$/);
  if (successMatch) return `LP ${successMatch[1]} — Success`;

  const failedMatch = pathname.match(/^\/(\d+|beeline\d+)\/failed$/);
  if (failedMatch) return `LP ${failedMatch[1]} — Failed`;

  const landingMatch = pathname.match(/^\/(\d+|beeline\d+)$/);
  if (landingMatch) return `LP ${landingMatch[1]} — Landing`;

  return pathname;
}

/**
 * Logs a page-view on every route change and sets the ambient page name
 * every subsequent log line will carry (see logger.setPageName) — mounted
 * once inside <Router> so every page component gets tracked without
 * needing its own logging call.
 */
function RouteViewLogger() {
  const location = useLocation();

  useEffect(() => {
    const pageName = resolvePageName(location.pathname);
    logger.setPageName(pageName, location.pathname);

    logger.info("page_view", "route_change", `Visitor opened the "${pageName}" page`, {
      path: location.pathname,
      query: redactParams(Object.fromEntries(new URLSearchParams(location.search))),
    });
  }, [location.pathname, location.search]);

  return null;
}

// Where an attempt ends: /<lp>/megacom/{success,failed}, /<lp>/beeline/{success,failed} and
// /<lp>/nur/failed (a number that belongs to neither carrier).
function carrierRoutes(lp, Success, Failed) {
  return [
    <Route key={`${lp}-megacom-success`} path={`/${lp}/megacom/success`} element={<Success />} />,
    <Route key={`${lp}-megacom-failed`} path={`/${lp}/megacom/failed`} element={<Failed />} />,
    <Route key={`${lp}-beeline-success`} path={`/${lp}/beeline/success`} element={<Success />} />,
    <Route key={`${lp}-beeline-failed`} path={`/${lp}/beeline/failed`} element={<Failed />} />,
    <Route key={`${lp}-nur-failed`} path={`/${lp}/nur/failed`} element={<Failed />} />,
  ];
}

function App() {
  return (
    <Router>
      <RouteViewLogger />
      <Routes>
        <Route path="/1089" element={<Page1089 />} />
        <Route path="/1089/success" element={<Page1089Success />} />
        <Route path="/1089/failed" element={<Page1089Failed />} />
        {carrierRoutes('1089', Page1089Success, Page1089Failed)}

        <Route path="/3346" element={<Page3346 />} />
        <Route path="/3346/success" element={<Page3346Success />} />
        <Route path="/3346/failed" element={<Page3346Failed />} />
        {carrierRoutes('3346', Page3346Success, Page3346Failed)}

        <Route path="/beeline1" element={<Beeline1 />} />
        <Route path="/beeline1/success" element={<Beeline1Success />} />
        <Route path="/beeline1/failed" element={<Beeline1Failed />} />
        {carrierRoutes('beeline1', Beeline1Success, Beeline1Failed)}

        <Route path="/beeline2" element={<Beeline2 />} />
        <Route path="/beeline2/success" element={<Beeline2Success />} />
        <Route path="/beeline2/failed" element={<Beeline2Failed />} />
        {carrierRoutes('beeline2', Beeline2Success, Beeline2Failed)}

        <Route path="/beeline3" element={<Beeline3 />} />
        <Route path="/beeline3/success" element={<Beeline3Success />} />
        <Route path="/beeline3/failed" element={<Beeline3Failed />} />
        {carrierRoutes('beeline3', Beeline3Success, Beeline3Failed)}
      </Routes>
    </Router>
  );
}

export default App;
